/**
 * Laenderkategorisierung der Lieferanten - Grundlage der EU/China-Faehnchen und des app-weiten
 * Laenderfilters (`components/Board/Bestellzeile.tsx`, `components/App/Kopfzeile.tsx`).
 *
 * NEUBAU ohne Bestandsvorbild: `index.html` kennt weder eine ISO-Laenderliste noch eine
 * Kategorisierung nach Lieferantenherkunft. Die Grundlage ist `Supplier.iso` (JTL `cISO`) - ein Feld
 * eines PARALLELEN Backend-Auftrags, das ueber die bestehende Entschachtelung
 * (`api/lieferanten.ts#entschachtele`) automatisch im Frontend landet, sobald der Backend-Auftrag
 * gemerged ist. Dieses Modul entwickelt GEGEN DEN TYP (`Supplier.iso?: string`) und behandelt
 * `undefined`/`null`/unbekannte Codes wie jeden anderen Lieferanten ohne Zuordnung - `"other"`, nie
 * ein Wurf.
 *
 * ================= Die geteilte Sichtbarkeitsregel (seit „Laenderfilter in die Kopfzeile") ========
 *
 * Der Filter selbst sass urspruenglich nur im Bestellboard (Zustand in `BoardSeite.tsx`, Chips in
 * `Board/Werkzeugleiste.tsx`) und war dort FUENFMAL verdrahtet gewesen, haette man ihn unveraendert
 * auf die vier anderen bestellungsfuehrenden Seiten kopiert. `istBestellungNachLaenderFilterSichtbar`
 * unten kapselt die Regel EINMAL, seither Zustand und Bedienung in der app-weiten Kopfzeile
 * (`App.tsx`/`Kopfzeile.tsx`), angewandt von Board, Uebersicht, Zeitstrahl, Zahlungen und Kosten.
 */
import { orderSupplier } from "./suppliers.ts";
import type { Order, Supplier } from "./types.ts";

/**
 * Die 27 EU-Mitgliedstaaten als ISO-3166-1-Alpha-2-Codes, GROSSBUCHSTABEN - der Vergleich in
 * `supplierRegion` normalisiert eingehende Codes deshalb ebenfalls auf Grossbuchstaben, statt sich
 * auf die Schreibweise von JTL zu verlassen.
 */
export const EU_ISO_CODES: readonly string[] = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI",
  "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU",
  "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE",
];

const EU_ISO_SET = new Set(EU_ISO_CODES);

/**
 * Die Region eines Lieferanten anhand seines ISO-Laendercodes (`Supplier.iso`, JTL `cISO`).
 * `"china"` bei `"CN"`, `"eu"` bei einem Treffer in `EU_ISO_CODES`, sonst `"other"` - das schliesst
 * `null`, `undefined`, einen leeren String und jeden unbekannten Code ein (z. B. `"US"`), damit ein
 * fehlendes oder ungepflegtes Feld nie einen Lieferanten faelschlich als EU oder China zeigt.
 */
export function supplierRegion(iso: string | null | undefined): "eu" | "china" | "other" {
  const code = (iso || "").trim().toUpperCase();
  if (!code) return "other";
  if (code === "CN") return "china";
  if (EU_ISO_SET.has(code)) return "eu";
  return "other";
}

/**
 * Der EU/China-Laenderfilter der Kopfzeile. Bis zum KI-Zugang (Stufe 1) stand der Typ in
 * `state/arbeitsplatz.ts`; er wohnt hier, weil `kostenAuswertung`/`uebersichtAuswertung` ihn in ihrer
 * Eingabe tragen und der Server kein Zustandsmodul (`localStorage`) in der Importkette haben darf.
 * `state/arbeitsplatz.ts` fuehrt den Namen als Re-Export weiter.
 */
export type LaenderFilter = { eu: boolean; china: boolean };

/**
 * Die Vorgabe „beide Regionen sichtbar" - bis zur Pruefrunde 2 (Befund G2) fuenfmal wortgleich als
 * lokale Konstante in `BoardSeite.tsx`/`KostenSeite.tsx`/`UebersichtSeite.tsx`/`ZahlungsSeite.tsx`/
 * `ZeitstrahlSeite.tsx` dupliziert. Seither hier EINMAL exportiert; die fuenf Seiten importieren sie.
 * Der Typ ist bewusst STRUKTURELL, aus demselben Grund wie bei `istBestellungNachLaenderFilterSichtbar`
 * oben (`lib/` haengt nicht von `state/` ab).
 */
export const LAENDERFILTER_VORGABE: LaenderFilter = { eu: true, china: true };

/**
 * Ob eine Bestellung nach dem EU/China-Laenderfilter sichtbar bleibt - die EINE Regel fuer alle
 * fuenf bestellungsfuehrenden Seiten (siehe Kopfkommentar, „Die geteilte Sichtbarkeitsregel"):
 * ausgeblendet wird, wenn die aufgeloeste Lieferanten-Region `"eu"` ist UND `laenderFilter.eu ===
 * false`, bzw. `"china"` ist UND `laenderFilter.china === false`. `"other"` (kein EU-Land, kein
 * China, unbekannt) bleibt IMMER sichtbar - fuer sie gibt es keinen Chip, der sie ausblenden koennte.
 *
 * Der Parametertyp des Filters ist bewusst STRUKTURELL (`{ eu; china }`) statt ein Import von
 * `state/arbeitsplatz.ts#LaenderFilter`: `lib/` haengt in diesem Baum nirgends von `state/` ab
 * (die Abhaengigkeit laeuft immer andersherum), und die beiden Typen sind zueinander zuweisbar.
 */
export function istBestellungNachLaenderFilterSichtbar(
  order: Order | null | undefined,
  suppliers: readonly Supplier[] | null | undefined,
  laenderFilter: LaenderFilter,
): boolean {
  const region = supplierRegion(orderSupplier(order, suppliers)?.iso);
  if (region === "eu" && !laenderFilter.eu) return false;
  if (region === "china" && !laenderFilter.china) return false;
  return true;
}
